// Virtual buddy companion model

// Animal type definitions with 6-stage emoji progressions
const ANIMAL_TYPES = {
  // Manual selection animals (7)
  bird: {
    name: 'Bird',
    selectable: true,
    rarity: 'common',
    emojis: ['egg', 'chick', 'birdling', 'bird', 'eagle', 'parrot']
  },
  dinosaur: {
    name: 'Dinosaur',
    selectable: true,
    rarity: 'common',
    emojis: ['egg', 'lizard', 'dino-young', 'sauropod', 'dragon-face', 'dragon']
  },
  penguin: {
    name: 'Penguin',
    selectable: true,
    rarity: 'common',
    emojis: ['egg', 'chick', 'penguin-young', 'penguin', 'penguin-crown', 'star-penguin']
  },
  bear: {
    name: 'Bear',
    selectable: true,
    rarity: 'common',
    emojis: ['egg', 'cub', 'teddy', 'bear', 'polar-bear', 'crowned-bear']
  },
  cat: {
    name: 'Cat',
    selectable: true,
    rarity: 'common',
    emojis: ['egg', 'kitten', 'cat', 'black-cat', 'lion', 'tiger']
  },
  dog: {
    name: 'Dog',
    selectable: true,
    rarity: 'common',
    emojis: ['egg', 'puppy', 'dog', 'service-dog', 'wolf', 'fox']
  },
  horse: {
    name: 'Horse',
    selectable: true,
    rarity: 'common',
    emojis: ['egg', 'foal', 'horse', 'racer', 'unicorn', 'star-unicorn']
  },
  // Grey egg RNG animals (1/10 chance) (3)
  rabbit: {
    name: 'Rabbit',
    selectable: false,
    rarity: 'common',
    emojis: ['egg', 'bunny', 'rabbit', 'bunny', 'crowned-bunny', 'sparkle-bunny']
  },
  turtle: {
    name: 'Turtle',
    selectable: false,
    rarity: 'common',
    emojis: ['egg', 'turtle', 'turtle', 'turtle', 'turtle', 'gem-turtle']
  },
  frog: {
    name: 'Frog',
    selectable: false,
    rarity: 'common',
    emojis: ['egg', 'froglet', 'frog', 'frog', 'crowned-frog', 'sparkle-frog']
  },
  // Neon egg RNG animals (1/100 chance) (3)
  phoenix: {
    name: 'Phoenix',
    selectable: false,
    rarity: 'rare',
    emojis: ['fire-egg', 'fire-chick', 'fire-birdling', 'fire-bird', 'blazing-eagle', 'star-phoenix']
  },
  spaceOwl: {
    name: 'Space Owl',
    selectable: false,
    rarity: 'rare',
    emojis: ['star-egg', 'owl', 'owl-sparkle', 'moon-owl', 'star-owl', 'galaxy-owl']
  },
  crystalFox: {
    name: 'Crystal Fox',
    selectable: false,
    rarity: 'rare',
    emojis: ['gem-egg', 'fox', 'gem-fox', 'sparkle-gem-fox', 'crystal-fox', 'star-gem-fox']
  },
  // Alien egg RNG animals (1/500 chance) (3)
  alien: {
    name: 'Alien',
    selectable: false,
    rarity: 'mythical',
    emojis: ['alien-egg', 'alien', 'ufo-alien', 'sparkle-ufo-alien', 'galaxy-alien', 'star-ufo-alien']
  },
  eldritchEntity: {
    name: 'Eldritch Entity',
    selectable: false,
    rarity: 'mythical',
    emojis: ['void-egg', 'eye', 'void-eye', 'octo-eye', 'galaxy-octo', 'star-void-eye']
  },
  timeDragon: {
    name: 'Time Dragon',
    selectable: false,
    rarity: 'mythical',
    emojis: ['clock-egg', 'clock-dragon', 'hourglass-dragon', 'sparkle-time-dragon', 'galaxy-time-dragon', 'star-time-dragon']
  }
};

// Egg color options
const EGG_COLORS = {
  // Standard selectable colors (10)
  blue: { name: 'Blue', selectable: true, hex: '#3b82f6' },
  red: { name: 'Red', selectable: true, hex: '#ef4444' },
  green: { name: 'Green', selectable: true, hex: '#10b981' },
  yellow: { name: 'Yellow', selectable: true, hex: '#eab308' },
  purple: { name: 'Purple', selectable: true, hex: '#a855f7' },
  orange: { name: 'Orange', selectable: true, hex: '#f97316' },
  brown: { name: 'Brown', selectable: true, hex: '#92400e' },
  black: { name: 'Black', selectable: true, hex: '#1f2937' },
  white: { name: 'White', selectable: true, hex: '#f3f4f6' },
  pink: { name: 'Pink', selectable: true, hex: '#ec4899' },
  // RNG-only special colors (3)
  grey: { name: 'Grey', selectable: false, hex: '#6b7280' },
  neon: { name: 'Neon', selectable: false, hex: '#00ff00' },
  alien: { name: 'Alien', selectable: false, hex: '#39ff14' }
};

export class Buddy {
  constructor(data = {}) {
    const parsedHappiness = Number(data.happiness);
    this.id = data.id || 'buddy_main';
    this.name = data.name || 'Dulce';
    this.level = data.level || 1;
    this.stage = data.stage || this.getStageFromLevel(this.level);
    this.happiness = Number.isFinite(parsedHappiness)
      ? Math.max(0, Math.min(100, parsedHappiness))
      : 100; // 0-100
    this.lastInteraction = data.lastInteraction || new Date().toISOString();
    
    // New multi-animal properties
    this.animalType = data.animalType || 'bird'; // Default to bird for migration
    this.eggColor = data.eggColor || 'blue'; // Default to blue for migration
    this.rarity = data.rarity || 'common'; // Default to common for migration
    this.isRandomRoll = data.isRandomRoll || false; // Was this obtained via RNG?
    
    // Migration: If old buddy data without animalType, apply defaults
    if (!data.animalType && data.level) {
      this.animalType = 'bird';
      this.eggColor = 'blue';
      this.rarity = 'common';
      this.isRandomRoll = false;
    }
  }

  // Evolution stages based on user level
  getStageFromLevel(level) {
    if (level < 5) return 'egg';
    if (level < 10) return 'hatchling';
    if (level < 20) return 'young';
    if (level < 35) return 'adult';
    if (level < 50) return 'elder';
    return 'legendary';
  }

  evolve(newLevel) {
    this.level = newLevel;
    const newStage = this.getStageFromLevel(newLevel);
    const evolved = newStage !== this.stage;
    this.stage = newStage;
    return evolved;
  }

  interact() {
    this.happiness = Math.min(100, this.happiness + 10);
    this.lastInteraction = new Date().toISOString();
  }

  decay() {
    // Decrease happiness over time if not interacted with
    const hoursSinceInteraction = 
      (Date.now() - new Date(this.lastInteraction)) / (1000 * 60 * 60);
    
    if (hoursSinceInteraction > 24) {
      this.happiness = Math.max(0, this.happiness - Math.floor(hoursSinceInteraction / 24) * 5);
    }
  }

  getEmoji() {
    const animalConfig = ANIMAL_TYPES[this.animalType];
    if (!animalConfig) {
      return 'egg'; // Fallback if animal type not found
    }
    
    const stageIndex = ['egg', 'hatchling', 'young', 'adult', 'elder', 'legendary'].indexOf(this.stage);
    if (stageIndex === -1) {
      return animalConfig.emojis[0]; // Fallback to egg
    }
    
    return animalConfig.emojis[stageIndex] || animalConfig.emojis[0];
  }
  
  getAnimalConfig() {
    return ANIMAL_TYPES[this.animalType] || ANIMAL_TYPES.bird;
  }
  
  getEggColorConfig() {
    return EGG_COLORS[this.eggColor] || EGG_COLORS.blue;
  }
  
  // Static method to roll for random buddy (RNG system)
  static rollRandomBuddy() {
    const roll = Math.random() * 1000; // 0-1000 range
    
    // 1/500 chance = 0.2% = roll < 2
    if (roll < 2) {
      const mythicalAnimals = ['alien', 'eldritchEntity', 'timeDragon'];
      const selectedAnimal = mythicalAnimals[Math.floor(Math.random() * mythicalAnimals.length)];
      return {
        animalType: selectedAnimal,
        eggColor: 'alien',
        rarity: 'mythical',
        isRandomRoll: true
      };
    }
    
    // 1/100 chance = 1% = roll < 10 (but > 2)
    if (roll < 12) { // 2-12 = 10 out of 1000 = 1%
      const rareAnimals = ['phoenix', 'spaceOwl', 'crystalFox'];
      const selectedAnimal = rareAnimals[Math.floor(Math.random() * rareAnimals.length)];
      return {
        animalType: selectedAnimal,
        eggColor: 'neon',
        rarity: 'rare',
        isRandomRoll: true
      };
    }
    
    // 1/10 chance = 10% = roll < 100 (but > 12)
    if (roll < 112) { // 12-112 = 100 out of 1000 = 10%
      const greyAnimals = ['rabbit', 'turtle', 'frog'];
      const selectedAnimal = greyAnimals[Math.floor(Math.random() * greyAnimals.length)];
      return {
        animalType: selectedAnimal,
        eggColor: 'grey',
        rarity: 'common',
        isRandomRoll: true
      };
    }
    
    // No special roll - return null so user picks manually
    return null;
  }
  
  // Get all selectable animals for first-time selection
  static getSelectableAnimals() {
    return Object.entries(ANIMAL_TYPES)
      .filter(([key, config]) => config.selectable)
      .map(([key, config]) => ({ key, ...config }));
  }
  
  // Get all selectable egg colors
  static getSelectableEggColors() {
    return Object.entries(EGG_COLORS)
      .filter(([key, config]) => config.selectable)
      .map(([key, config]) => ({ key, ...config }));
  }

  getMood() {
    if (this.happiness >= 80) return 'happy';
    if (this.happiness >= 50) return 'neutral';
    if (this.happiness >= 20) return 'sad';
    return 'unhappy';
  }
}
